import { apiClient } from "./client";
import { EsitoInvio } from "./report";

/**
 * Il report amministrativo OdA/OdV — `GET /api/report-ordini`, solo admin
 * (`report_ordini:read`).
 *
 * Le righe arrivano GIÀ divise in sezioni e già ordinate: il server è l'unico
 * posto che sa leggere tutte e quattro le tabelle, e rifare qui la divisione
 * significherebbe tenere allineate due copie delle stesse tre condizioni.
 */

/** Da quale dei quattro moduli arriva l'attività. */
export type TipoOrigine = "installazione" | "attivazione" | "intervento" | "sopralluogo";

/** Un'attività coperta da un ordine. */
export interface AttivitaOrdine {
  /** Id del record di provenienza, per il link `?open=<id>` alla sua pagina. */
  id: string;
  tipo: TipoOrigine;
  /** Come si riconosce il record: titolo dell'intervento, insegna del negozio. */
  etichetta: string | null;
  cliente: string | null;
  sede: string | null;
  /**
   * Lo stato del record di provenienza, alla lettera come sta in tabella.
   *
   * NON e' confrontabile fra moduli, ed e' il motivo per cui e' una stringa e
   * non un'unione: ogni modulo ha il suo vocabolario ("Effettuata" per
   * un'installazione, "Attiva" per un'attivazione, "Approvato" per un
   * sopralluogo) e quello degli INTERVENTI non e' nemmeno nostro — e' il nome
   * dello stage configurato in Odoo, che puo' cambiare senza che l'app lo sappia.
   * Nullable perche' su `interventi` la colonna lo e'.
   */
  stato: string | null;
  /** ISO, o `null` per i record che non hanno né data effettiva né prevista. */
  data: string | null;
}

/**
 * Una riga del report: un ORDINE, con le attività che copre (issue #211, PR
 * A). Prima la riga era l'attività, e un ordine su due attività compariva due
 * volte.
 */
export interface RigaOrdine {
  ordine_id: string;
  /** L'OdA nella prima sezione, l'OdV nelle altre quattro. */
  numero: string;
  /** La data dell'attività più vecchia, ISO; `null` se nessuna ne ha una. */
  data: string | null;
  /**
   * Perché l'ordine sta in questa sezione, quando la sezione da sola non lo
   * dice: i conflitti sugli interventi annullati, e quante attività tengono un
   * OdV fra i «non ancora fatturabili». `null` altrove.
   */
  motivo: string | null;
  /** Le attività coperte, dalla più vecchia. Mai vuoto. */
  attivita: AttivitaOrdine[];
}

export interface ReportOrdini {
  /** OdA con almeno un'attività e la fattura del fornitore non ancora arrivata. */
  oda: RigaOrdine[];
  /**
   * I quattro gruppi OdV, che sono una PARTIZIONE degli OdV con almeno
   * un'attività (tranne i già fatturati): ognuno sta in uno e uno solo, e
   * nessun gruppo viene nascosto quando è vuoto — «nessun OdV da qualificare» è
   * una risposta, e va vista.
   */
  odv: {
    da_fatturare: RigaOrdine[];
    /**
     * Una fattura ci andrebbe (non in garanzia, non emessa) ma almeno
     * un'attività è in uno stato che non permette ancora di emetterla. Distinto da
     * `da_qualificare`: lì manca la decisione sulla garanzia, qui manca
     * l'avanzamento del lavoro.
     */
    non_ancora_fatturabili: RigaOrdine[];
    non_da_fatturare: RigaOrdine[];
    da_qualificare: RigaOrdine[];
  };
}

export async function getReportOrdini(): Promise<ReportOrdini> {
  const { data } = await apiClient.get<ReportOrdini>("/report-ordini");
  return data;
}

/**
 * Manda il report per email, come allegato Excel.
 *
 * I destinatari viaggiano nel BODY e non arrivano da una configurazione: questo
 * report e' on-demand, e chi lo manda sceglie a chi ogni volta. La lista fissa
 * di /settings/tipi-report serve ai report che partono da soli — vedi la nota
 * in testa a `backend/src/routes/reportOrdini.ts`.
 *
 * Il foglio NON si manda da qui: lo rigenera il server dai dati suoi. Quello che
 * si spedisce dev'essere quello che il server sa, non quello che il browser ha
 * in memoria da quando la pagina e' stata caricata.
 */
export async function inviaReportOrdini(destinatari: string[], nota: string): Promise<EsitoInvio> {
  const { data } = await apiClient.post<EsitoInvio>("/report-ordini/invia", { destinatari, nota });
  return data;
}
